/**
 * The team's way into the client's inspiration board (docs/INSPIRATION_BOARD.md).
 *
 *   InspirationRows      — the children of Reference > Design > Client Inspiration: one outline row
 *                          per board ("All pictures", each named board, "Unsorted"), each with its
 *                          count. Clicking a row opens the board at that tab. Counts only — the
 *                          masonry board itself is never rendered inline (outline-first rule).
 *   InspirationBoardHost — mounted once in OutlineView; opens the full-screen board on
 *                          'open-inspiration-board-team' { projectId, projectName?, tab? }.
 *   openTeamInspirationBoard(projectId, tab) — dispatch helper.
 *
 * InspirationRows follows the lazy-load rules: fetch on mount when expanded, loaded flag is a ref,
 * and the board's close fires 'inspiration-updated' so counts refresh.
 */

import { useEffect, useRef, useState } from 'react'
import { ctx } from '../../outline/ctx.js'
import { getBoardSummary } from '../../services/inspiration.js'
import InspirationBoard from './InspirationBoard.jsx'

export function openTeamInspirationBoard(projectId, tab = 'all') {
  const projectName = (ctx.state?.milestones || [])[0]?.fields?.['Project Name'] || ''
  window.dispatchEvent(new CustomEvent('open-inspiration-board-team', { detail: { projectId, projectName, tab } }))
}

function ago(iso) {
  if (!iso) return ''
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  return days <= 0 ? 'today' : days === 1 ? 'yesterday' : days + ' days ago'
}

/** Summary cache shared across mounts of this node, keyed by project id. */
const cache = {}

export function useInspirationSummary(projectId, active) {
  const [, bump] = useState(0)
  const hasLoaded = useRef(false)
  useEffect(() => { hasLoaded.current = false }, [projectId])

  function load(force) {
    if (!projectId || (hasLoaded.current && !force)) return
    hasLoaded.current = true
    getBoardSummary(projectId)
      .then(s => { cache[projectId] = s; bump(v => v + 1) })
      .catch(() => { cache[projectId] = cache[projectId] || { total: 0, unsorted: 0, favorites: 0, collections: [], error: true }; bump(v => v + 1) })
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (active) load(false) }, [active, projectId])
  useEffect(() => {
    const on = e => { if (!e.detail || e.detail.projectId === projectId) load(true) }
    window.addEventListener('inspiration-updated', on)
    return () => window.removeEventListener('inspiration-updated', on)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])
  return projectId ? cache[projectId] : null
}

function Row({ label, meta, onOpen, rowKey }) {
  return (
    <div className="wf-node wf-node--item" data-key={rowKey}>
      <div className="wf-row">
        <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
        <button className="wf-bullet wf-bullet--item" title={`Open ${label}`} onClick={onOpen}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text" style={{ cursor: 'pointer' }} onClick={onOpen}>{label}</span>
          {meta && <span className="wf-prog" style={{ color: 'var(--aj-text-muted)', marginLeft: 8 }}>{meta}</span>}
        </div>
      </div>
    </div>
  )
}

export function InspirationRows({ projectId, summary }) {
  if (!summary) return <div className="wf-loading">Loading inspiration...</div>
  const open = tab => openTeamInspirationBoard(projectId, tab)
  if (!summary.total && !summary.collections.length) {
    return (
      <Row rowKey={`insp:${projectId}:all`} label="Open the board"
        meta="Nothing yet. Add a Client Hub entry to invite the client." onOpen={() => open('all')} />
    )
  }
  return (
    <>
      <Row rowKey={`insp:${projectId}:all`} label="All pictures"
        meta={summary.total + (summary.latest ? ' · last added ' + ago(summary.latest) : '')} onOpen={() => open('all')} />
      {summary.favorites > 0 && <Row rowKey={`insp:${projectId}:fav`} label="Client favorites" meta={String(summary.favorites)} onOpen={() => open('fav')} />}
      {summary.collections.map(c => (
        <Row key={c.id} rowKey={`insp:${projectId}:${c.id}`} label={c.name} meta={String(c.count)} onOpen={() => open(c.id)} />
      ))}
      {summary.unsorted > 0 && summary.collections.length > 0 && (
        <Row rowKey={`insp:${projectId}:unsorted`} label="Unsorted" meta={String(summary.unsorted)} onOpen={() => open('unsorted')} />
      )}
    </>
  )
}

export function InspirationBoardHost() {
  const [open, setOpen] = useState(null)
  useEffect(() => {
    const on = e => setOpen(e.detail || null)
    window.addEventListener('open-inspiration-board-team', on)
    // The team's notice email links to /project?recordId=…&inspiration=1: open the board on arrival.
    const q = new URLSearchParams(window.location.search)
    const rid = q.get('recordId')
    if (q.get('inspiration') === '1' && rid) {
      setOpen({ projectId: rid, projectName: '', tab: 'all' })
      q.delete('inspiration')
      window.history.replaceState(window.history.state, '', window.location.pathname + '?' + q.toString())
    }
    return () => window.removeEventListener('open-inspiration-board-team', on)
  }, [])
  if (!open || !open.projectId) return null
  return (
    <InspirationBoard key={open.projectId + ':' + open.tab} scope={{ projectId: open.projectId }} mode="team"
      projectName={open.projectName} initialTab={open.tab || 'all'}
      onClose={() => {
        window.dispatchEvent(new CustomEvent('inspiration-updated', { detail: { projectId: open.projectId } }))
        setOpen(null)
      }} />
  )
}
